import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApi } from "../../api";
import { Modal, Notice } from "../../lib/ui";

/*
 * Edit client – port of update_client.php (API: GET/PUT /api/clients/<id>, backend/records.py)
 * Route: /update_client/:id   Client name is required; email, phone and address are optional.
 */

const BLANK = { client_name: "", contact_email: "", contact_phone: "", address: "" };

export default function EditClient({ user }) {
  const { id } = useParams();
  const api = useApi();
  const [form, setForm] = useState(BLANK);
  const [loaded, setLoaded] = useState(false);
  const [fatal, setFatal] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  const remove = async () => {
    setConfirmDelete(false);
    try { await api.request(`/api/clients/${id}`, "DELETE"); navigate("/client", { replace: true }); }
    catch (err) { setNotice({ tone: "err", text: err.message }); }
  };

  useEffect(() => {
    if (!/^\d+$/.test(id || "")) { setFatal("Invalid client ID."); return; }
    api.get(`/api/clients/${id}`)
      .then((c) => { setForm({ ...BLANK, ...Object.fromEntries(Object.entries(c).map(([k, v]) => [k, v ?? ""])) }); setLoaded(true); })
      .catch((e) => setFatal(e.message));
  }, [api, id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setNotice(null);
    try {
      const r = await api.request(`/api/clients/${id}`, "PUT", form);
      setNotice({ tone: "ok", text: r.message });
    } catch (err) { setNotice({ tone: "err", text: err.message }); }
    finally { setSaving(false); }
  };

  return (
    <main className="pg" style={{ maxWidth: "46rem" }}>
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/dashboard">Home</Link><i className="fas fa-chevron-right" /><Link to="/client">Clients</Link>
        <i className="fas fa-chevron-right" /><span aria-current="page">Edit client</span>
      </nav>
      <div className="pg-head"><div className="pg-title">
        <span className="chip"><i className="fas fa-user-pen text-blue-500" /></span>
        <div><h1>Edit client</h1><p>{loaded ? form.client_name : "Update contact details"}</p></div>
      </div></div>

      {fatal ? <Notice notice={{ tone: "err", text: fatal }} /> : (
        <div className="panel">
          <form className="form" onSubmit={save} style={{ maxWidth: "none" }}>
            <div className="field wide"><label htmlFor="ec-name">Client name<span className="req" aria-hidden="true">*</span></label>
              <input id="ec-name" value={form.client_name} onChange={set("client_name")} required disabled={!loaded} /></div>
            <div className="field"><label htmlFor="ec-email">Email</label>
              <input id="ec-email" type="email" value={form.contact_email} onChange={set("contact_email")} disabled={!loaded} /></div>
            <div className="field"><label htmlFor="ec-phone">Phone</label>
              <input id="ec-phone" type="tel" value={form.contact_phone} onChange={set("contact_phone")} disabled={!loaded} /></div>
            <div className="field wide"><label htmlFor="ec-addr">Address</label>
              <textarea id="ec-addr" rows={3} value={form.address} onChange={set("address")} disabled={!loaded} /></div>
            <div className="form-actions">
              <button type="submit" className="btn primary" disabled={!loaded || saving}>{saving ? "Saving…" : "Update client"}</button>
              <Notice notice={notice} />
              {user?.role === "admin" && <button type="button" className="btn danger" style={{ marginLeft: "auto" }} onClick={() => setConfirmDelete(true)} disabled={!loaded}><i className="fas fa-trash-can" /> Delete client</button>}
            </div>
          </form>
        </div>
      )}
      {confirmDelete && (
        <Modal title="Delete this client?" onClose={() => setConfirmDelete(false)}
          actions={<><button type="button" className="btn" onClick={() => setConfirmDelete(false)}>Cancel</button><button type="button" className="btn danger" onClick={remove}>Yes, delete</button></>}>
          <b>{form.client_name}</b> will be removed. Clients with payments, invoices or sales can't be deleted.
        </Modal>
      )}
    </main>
  );
}
